import { Feature } from 'ol';
import Geometry from 'ol/geom/Geometry';
import RenderFeature from 'ol/render/Feature';
import { Fill, Icon, Stroke, Style, Text } from 'ol/style';
import ImageStyle from 'ol/style/Image';
import { StyleFunction } from 'ol/style/Style';
import {
  ASSET_DATA,
  ASSET_LAYER,
  IS_VISIBLE,
  MAGNIFY,
  PIN_ELEMENT_SCALE,
} from '../../../util/constants';
import { Color } from 'ol/color';
import { ZIndexes } from '../../../util/ZIndexes';
import CircleStyle from 'ol/style/Circle';
import { ACTIVE_GREEN, RED, WHITE } from '../../../Style/GeoMobyBaseTheme';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { LayerParts } from '../types';
import { AssetEntity } from '../../../util/enums';

/* Icons */
export const iconFromElement = (
  el: HTMLImageElement,
  w: number,
  h: number,
  ax: number,
  ay: number,
  options?: {
    scale?: number;
    opcaity?: number;
    color?: Color | string;
  },
): Icon => {
  return new Icon({
    img: el,
    imgSize: [w, h],
    anchor: [ax, ay],
    scale: options?.scale === undefined ? 1 : options.scale,
    opacity: options?.opcaity === undefined ? 1 : options.opcaity,
    color: options?.color,
  });
};

const pinFromElement = (id: string) =>
  iconFromElement(document.getElementById(id) as HTMLImageElement, 512, 512, 0.5, 1, {
    scale: PIN_ELEMENT_SCALE,
  });
const beaconPurpleIcon = pinFromElement('beaconPurple');
const beaconRedIcon = pinFromElement('beaconRed');
const deviceGreenIcon = pinFromElement('deviceGreen');
const deviceOrangeIcon = pinFromElement('deviceOrange');
const deviceBlueIcon = pinFromElement('deviceBlue');
const deviceRedIcon = pinFromElement('deviceRed');
const gpsTrackerBlueIcon = pinFromElement('gpsTrackerBlue');
const gpsTrackerRedIcon = pinFromElement('gpsTrackerRed');
const toolPurpleIcon = pinFromElement('toolPurple');
const toolRedIcon = pinFromElement('toolRed');

const beaconIcon = (selected: boolean): ImageStyle => {
  return selected ? beaconRedIcon : beaconPurpleIcon;
};
const deviceIcon = (selected: boolean): ImageStyle => {
  return selected ? deviceRedIcon : deviceBlueIcon;
};
const gpsTrackerIcon = (selected: boolean): ImageStyle => {
  return selected ? gpsTrackerRedIcon : gpsTrackerBlueIcon;
};
const toolIcon = (selected: boolean): ImageStyle => {
  return selected ? toolRedIcon : toolPurpleIcon;
};

const assetIcon = (
  selected: boolean,
  wfCheck: boolean | undefined,
  type: AssetEntity,
): ImageStyle => {
  if (wfCheck !== undefined) {
    return wfCheck ? deviceGreenIcon : deviceOrangeIcon;
  }
  return type === AssetEntity.Device
    ? deviceIcon(selected)
    : type === AssetEntity.Tool
    ? toolIcon(selected)
    : beaconIcon(selected);
};

/* Count */
export const countStyle = (
  count: number,
  options?: {
    radius?: number;
    offset_x?: number;
    offset_y?: number;
    text_offset_x?: number;
    text_offset_y?: number;
    active?: boolean;
  },
): Style =>
  new Style({
    image: new CircleStyle({
      displacement: [12.5, 13],
      radius: count === 0 ? 6 : 10,
      fill: new Fill({
        color: options?.active ? ACTIVE_GREEN : RED,
      }),
    }),
    text: new Text({
      text: count === 0 ? '' : count > 99 ? '99+' : count.toString(),
      fill: new Fill({
        color: WHITE,
      }),
      font: `10px Open Sans, Montserrat, Arial, sans-serif`,
      stroke: new Stroke({
        color: WHITE,
        width: 1,
      }),
      offsetX: 13,
      offsetY: -13,
      scale: 0.9,
    }),
    zIndex: ZIndexes.COUNTER,
  });

/* Asset */
const styleFromAssetId = (
  type: AssetEntity,
  selected: boolean,
  opacity: 0 | 1,
  magnifed: boolean,
  wfCheck: boolean | undefined,
  recentSensedCount?: number,
): Style[] => {
  const image =
    type === AssetEntity.GpsTracker ? gpsTrackerIcon(selected) : assetIcon(selected, wfCheck, type);
  const scale = magnifed
    ? 0.12
    : type === AssetEntity.Beacon || type === AssetEntity.Tool
    ? 0.04
    : 0.08;
  image.setScale(scale);
  image.setOpacity(opacity);
  const styles = [
    new Style({
      image,
      zIndex: ZIndexes.MARKER,
    }),
  ];

  if (recentSensedCount && recentSensedCount > 0) {
    styles.push(countStyle(recentSensedCount, { active: true }));
  }
  return styles;
};
export const getImageFromStyle = (style: Style | Style[] | undefined): ImageStyle | undefined => {
  if (!style) return undefined;

  if (Array.isArray(style)) {
    for (const s of style) {
      const image = s.getImage();
      if (image) return image;
    }
    return undefined;
  }

  return style.getImage() ?? undefined;
};
export const setImageScale = (style: Style | Style[] | undefined, scale: number): void => {
  if (!style) return;

  if (Array.isArray(style)) {
    for (const s of style) {
      const image = s.getImage();
      if (image) image.setScale(scale);
    }
  } else {
    const image = style.getImage();
    if (image) image.setScale(scale);
  }
};
export const setImageOpacity = (style: Style | Style[] | undefined, opacity: number) => {
  if (!style) return;

  if (Array.isArray(style)) {
    for (const s of style) {
      const image = s.getImage();
      if (image) image.setOpacity(opacity);
    }
  } else {
    const image = style.getImage();
    if (image) image.setOpacity(opacity);
  }
};
export const assetStyle = (cache: Map<string, Style | Style[]>): StyleFunction => {
  return (asset: Feature<Geometry> | RenderFeature) => {
    let style: Style | Style[] | undefined;

    const assetData = asset.get(ASSET_DATA);
    if (!assetData) return undefined;

    const { type, recentSensedCount } = assetData;
    const isMagnifed = asset.get(MAGNIFY) ?? false;
    const isVisible = asset.get(IS_VISIBLE) ?? true;
    const isSelected = asset.get('selected') ?? false;
    const isWfCheck = asset.get('wfCheck') ?? undefined;
    const selected = isSelected ? 'selected' : 'not_selected';
    const wfCheck =
      isWfCheck === true ? 'ok' : isWfCheck === false ? 'notok' : 'not in a welfare check';
    const key = `${type}_${selected}_${wfCheck}_${recentSensedCount}`;

    if (cache.has(key)) {
      style = cache.get(key);
    } else {
      style = styleFromAssetId(type, isSelected, isVisible, isMagnifed, isWfCheck);

      if (style) {
        cache.set(key, style);
      }
    }
    const opacity = isVisible ? 1 : 0;
    const scale = isMagnifed
      ? 0.12
      : type === AssetEntity.Beacon || type === AssetEntity.Tool
      ? 0.04
      : 0.08;

    setImageOpacity(style, opacity);
    setImageScale(style, scale);
    return style;
  };
};
export const initialiseAssetOverlay = (): LayerParts => {
  const source = new VectorSource();
  return {
    src: source,
    lyr: new VectorLayer({
      source,
      className: ASSET_LAYER,
      style: assetStyle(new Map<string, Style>()),
      zIndex: ZIndexes.MARKER,
    }),
  };
};
